import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { History, Sparkles } from 'lucide-react'
import { toast } from 'sonner'

import {
  AuthoringCaseFile,
  AuthoringDocument,
  BlockPalette,
  ConfigBlockChrome,
  type AuthoringBlockInstance,
} from '@/components/authoring'
import {
  CLASSIFIER_BLOCK,
  blocksToPayload,
  classifierPaletteItems,
  createBlockFromPaletteType,
  payloadToBlocks,
  type ClassifierDraftPayload,
} from '@/components/admin/classifiers/classifier-blocks'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { EntityManageSection } from '@/components/ui/entity-manage-section'
import { ListViewSwitcher } from '@/components/filters/list-view-switcher'
import {
  useClassifierCorpus,
  useClassifierPreview,
  usePublishClassifierRevision,
  type ClassifierOutputContract,
} from '@/hooks/use-classifiers-admin'

function contractFieldList(contract: ClassifierOutputContract | null | undefined) {
  const props = contract?.output_json_schema?.properties
  if (!props || typeof props !== 'object') return [] as string[]
  return Object.keys(props)
}

function StructuredPreviewOutput({
  output,
  contract,
}: {
  output: Record<string, unknown>
  contract: ClassifierOutputContract | null | undefined
}) {
  const fields = contractFieldList(contract)
  const keys = fields.length > 0 ? fields : Object.keys(output)
  return (
    <ul className="space-y-2">
      {keys.map((key) => {
        const value = output[key]
        const enumLabels = contract?.enum_labels?.[key]
        if (typeof value === 'string' && enumLabels?.[value]) {
          return (
            <li key={key} className="rounded-md border border-border px-2 py-1.5 text-sm">
              <p className="font-mono text-xs text-muted-foreground">{key}</p>
              <Badge variant="secondary" className="mt-1">
                {enumLabels[value]}
              </Badge>
              <span className="ml-2 text-xs text-muted-foreground">({value})</span>
            </li>
          )
        }
        if (Array.isArray(value)) {
          return (
            <li key={key} className="rounded-md border border-border px-2 py-1.5 text-sm">
              <p className="font-mono text-xs text-muted-foreground">{key}</p>
              <ul className="mt-1 list-disc pl-4 text-xs">
                {value.map((item, i) => (
                  <li key={i}>
                    {typeof item === 'object' && item !== null
                      ? JSON.stringify(item)
                      : String(item)}
                  </li>
                ))}
              </ul>
            </li>
          )
        }
        return (
          <li key={key} className="rounded-md border border-border px-2 py-1.5 text-sm">
            <p className="font-mono text-xs text-muted-foreground">{key}</p>
            <p className="mt-0.5 whitespace-pre-wrap text-sm">{String(value ?? '')}</p>
          </li>
        )
      })}
    </ul>
  )
}

export function ClassifierBuilderPage() {
  const { corpusId } = useParams<{ corpusId: string }>()
  const navigate = useNavigate()
  const { data: corpus, isLoading, error } = useClassifierCorpus(corpusId)
  const publish = usePublishClassifierRevision(corpusId)
  const preview = useClassifierPreview()

  const [label, setLabel] = useState('')
  const [debounceSeconds, setDebounceSeconds] = useState('12')
  const [changeSummary, setChangeSummary] = useState('')
  const [blocks, setBlocks] = useState<AuthoringBlockInstance[]>([])
  const [basePayload, setBasePayload] = useState<ClassifierDraftPayload>({})
  const [transcript, setTranscript] = useState('')
  const [previewOut, setPreviewOut] = useState<Record<string, unknown> | null>(null)
  const [previewView, setPreviewView] = useState<'structured' | 'json'>('structured')
  const [followupPreview, setFollowupPreview] = useState(false)
  const [priorFields, setPriorFields] = useState<Record<string, string>>({})

  const authoringMode = corpus?.authoring_mode === 'advanced' ? 'advanced' : 'safe'
  const isAdvanced = authoringMode === 'advanced'
  const contract = corpus?.contract ?? null

  useEffect(() => {
    setPreviewOut(null)
    setFollowupPreview(false)
    setPriorFields({})
  }, [corpusId])

  useEffect(() => {
    if (!corpus) return
    const payload = (corpus.current_revision?.payload || {}) as ClassifierDraftPayload
    setBasePayload(payload)
    setLabel(String(payload.label || corpus.label || ''))
    setDebounceSeconds(String(payload.debounce_seconds ?? 12))
    setBlocks(payloadToBlocks(payload, corpus.contract))
    if (corpus.preview_transcript) {
      setTranscript((prev) => prev || corpus.preview_transcript || '')
    }
  }, [corpus])

  const paletteItems = useMemo(
    () =>
      classifierPaletteItems({
        blocks,
        contract,
        profileId: corpus?.profile_id || '',
      }),
    [blocks, contract, corpus?.profile_id],
  )

  const draftPayload = useMemo(
    () =>
      blocksToPayload(blocks, basePayload, {
        label,
        debounceSeconds: Number(debounceSeconds) || 12,
      }),
    [blocks, basePayload, label, debounceSeconds],
  )

  const onPublish = async () => {
    try {
      await publish.mutateAsync({
        payload: draftPayload,
        change_summary: changeSummary.trim(),
      })
      toast.success('Published new revision')
      setChangeSummary('')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Publish failed')
    }
  }

  const onPreview = async () => {
    if (!corpusId) return
    const prior_output: Record<string, unknown> = {}
    if (followupPreview) {
      for (const [k, v] of Object.entries(priorFields)) {
        if (!v.trim()) continue
        try {
          prior_output[k] = JSON.parse(v)
        } catch {
          prior_output[k] = v
        }
      }
    }
    try {
      const result = await preview.mutateAsync({
        corpus_id: corpusId,
        payload: draftPayload,
        transcript,
        prior_output,
      })
      setPreviewOut(result.output)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Preview failed')
    }
  }

  const updateBlockParams = (id: string, patch: Record<string, unknown>) => {
    setBlocks((prev) =>
      prev.map((b) => (b.id === id ? { ...b, params: { ...b.params, ...patch } } : b)),
    )
  }

  if (isLoading) {
    return (
      <PageContainer>
        <p className="text-sm text-muted-foreground">Loading classifier…</p>
      </PageContainer>
    )
  }

  if (error || !corpus) {
    return (
      <PageContainer>
        <p className="text-sm text-destructive">
          {(error as Error)?.message || 'Corpus not found'}
        </p>
        <Button
          type="button"
          variant="ghostMuted"
          className="mt-2"
          onClick={() => navigate('/platform/classifiers')}
        >
          Back
        </Button>
      </PageContainer>
    )
  }

  const fieldKeys = contractFieldList(contract)

  return (
    <PageContainer>
      <AuthoringCaseFile
        header={
          <PageHeader
            leading={<PageHeaderLeading />}
            title={corpus.label}
            description={`${corpus.profile_id} · ${corpus.capability_class} · ${authoringMode} authoring · current v${corpus.current_revision?.version ?? '—'}`}
            actions={
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/platform/classifiers')}
                >
                  Back
                </Button>
                <Button type="button" onClick={() => void onPublish()} disabled={publish.isPending}>
                  Publish
                </Button>
              </div>
            }
          />
        }
        palette={
          <BlockPalette
            title="Add block"
            items={paletteItems}
            onAdd={(type) => {
              const created = createBlockFromPaletteType(type, contract)
              if (!created) return
              setBlocks((prev) => [...prev, created])
            }}
          />
        }
        document={
          <div className="space-y-4">
            <div className="grid gap-3 @md:grid-cols-3">
              <div className="space-y-1 @md:col-span-2">
                <Label htmlFor="clf-label">Label</Label>
                <Input id="clf-label" value={label} onChange={(e) => setLabel(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="clf-debounce">Debounce (s)</Label>
                <Input
                  id="clf-debounce"
                  type="number"
                  min={0}
                  value={debounceSeconds}
                  onChange={(e) => setDebounceSeconds(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="clf-change">Change summary</Label>
              <Input
                id="clf-change"
                value={changeSummary}
                onChange={(e) => setChangeSummary(e.target.value)}
                placeholder="What changed in this revision?"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Role / mission is author prose only. At runtime the platform appends output format,
              field guidance, enum meanings, and safety from the code-owned contract and your
              blocks. Prior conversation state is injected separately.
              {isAdvanced ? ' Advanced mode: larger role editor.' : ''}
            </p>

            <AuthoringDocument
              blocks={blocks}
              onReorder={setBlocks}
              renderBlock={(block, drag) => {
                const pinned = Boolean(block.pinned)
                if (block.type === CLASSIFIER_BLOCK.contractOverview) {
                  return (
                    <ConfigBlockChrome
                      kindLabel="Output contract"
                      title="Immutable characteristics"
                      locked
                      hideDrag
                      hideRemove
                      setNodeRef={drag.setNodeRef}
                      style={drag.style}
                    >
                      <div className="space-y-2 text-sm">
                        <p>
                          <span className="text-muted-foreground">Profile</span>{' '}
                          <code className="text-xs">{corpus.profile_id}</code>
                        </p>
                        <p>
                          <span className="text-muted-foreground">Capability</span>{' '}
                          {corpus.capability_class}
                        </p>
                        {contract?.mirror_targets?.length ? (
                          <div>
                            <p className="mb-1 text-muted-foreground">Board mirror targets</p>
                            <ul className="list-disc pl-5 text-xs">
                              {contract.mirror_targets.map((t) => (
                                <li key={t}>
                                  <code>{t}</code>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                        <div>
                          <p className="mb-1 text-muted-foreground">Output fields</p>
                          <ul className="space-y-1.5">
                            {fieldKeys.map((key) => (
                              <li
                                key={key}
                                className="rounded-md border border-border px-2 py-1.5"
                              >
                                <p className="font-mono text-xs font-medium">{key}</p>
                                {contract?.field_help?.[key] ? (
                                  <p className="text-xs text-muted-foreground">
                                    {contract.field_help[key]}
                                  </p>
                                ) : null}
                                {contract?.enum_labels?.[key] ? (
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {Object.entries(contract.enum_labels[key]).map(([v, lbl]) => (
                                      <Badge key={v} variant="outline" className="text-xs">
                                        {lbl}
                                      </Badge>
                                    ))}
                                  </div>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </ConfigBlockChrome>
                  )
                }

                const kindLabel =
                  block.type === CLASSIFIER_BLOCK.systemGuidance
                    ? 'Role / mission'
                    : block.type === CLASSIFIER_BLOCK.followupGuidance
                      ? 'Follow-up guidance'
                      : block.type === CLASSIFIER_BLOCK.safetyNotes
                        ? 'Safety notes'
                        : block.type === CLASSIFIER_BLOCK.fieldHelp
                          ? `Field help · ${String(block.params.field || '')}`
                          : block.type === CLASSIFIER_BLOCK.enumLabels
                            ? `Enum labels · ${String(block.params.field || '')}`
                            : block.type.replace(/_/g, ' ')

                return (
                  <ConfigBlockChrome
                    kindLabel={kindLabel}
                    hideDrag={pinned}
                    hideRemove={pinned}
                    setNodeRef={drag.setNodeRef}
                    style={drag.style}
                    isDragging={drag.isDragging}
                    dragHandleProps={pinned ? undefined : drag.dragHandleProps}
                    onRemove={
                      pinned
                        ? undefined
                        : () => setBlocks((prev) => prev.filter((b) => b.id !== block.id))
                    }
                  >
                    {block.type === CLASSIFIER_BLOCK.enumLabels ? (
                      <div className="space-y-2">
                        {Object.entries(
                          (block.params.labels as Record<string, string>) || {},
                        ).map(([enumVal, enumLabel]) => (
                          <div key={enumVal} className="space-y-1">
                            <Label className="font-mono text-xs">{enumVal}</Label>
                            <Input
                              value={enumLabel}
                              onChange={(e) => {
                                const next = {
                                  ...((block.params.labels as Record<string, string>) || {}),
                                  [enumVal]: e.target.value,
                                }
                                updateBlockParams(block.id, { labels: next })
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Textarea
                        className={
                          isAdvanced && block.type === CLASSIFIER_BLOCK.systemGuidance
                            ? 'min-h-32 text-sm'
                            : block.type === CLASSIFIER_BLOCK.systemGuidance
                              ? 'min-h-20 text-sm'
                              : 'min-h-24 text-sm'
                        }
                        value={String(block.params.text ?? '')}
                        onChange={(e) => updateBlockParams(block.id, { text: e.target.value })}
                        placeholder={
                          block.type === CLASSIFIER_BLOCK.systemGuidance
                            ? 'e.g. You triage veterinary phone calls for operators.'
                            : undefined
                        }
                      />
                    )}
                    {block.type === CLASSIFIER_BLOCK.systemGuidance ? (
                      <p className="text-xs text-muted-foreground">
                        Do not paste JSON field lists here — use field help / enum / safety blocks;
                        the runner composes those automatically.
                      </p>
                    ) : null}
                    {block.type === CLASSIFIER_BLOCK.followupGuidance ? (
                      <p className="text-xs text-muted-foreground">
                        Prior summary and state are appended by the runner — no template placeholders.
                      </p>
                    ) : null}
                  </ConfigBlockChrome>
                )
              }}
            />
          </div>
        }
        below={
          <>
            <EntityManageSection
              title="Preview"
              subtitle="Run against a sample transcript without publishing"
              leadingIcon={Sparkles}
              defaultOpen
            >
              <div className="space-y-3">
                <Textarea
                  className="min-h-24 text-sm"
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  placeholder="Tenant sample transcript"
                />
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={followupPreview}
                    onChange={(e) => setFollowupPreview(e.target.checked)}
                  />
                  Follow-up turn (send prior output)
                </label>
                {followupPreview ? (
                  <div className="grid gap-2 @md:grid-cols-2">
                    {(fieldKeys.length ? fieldKeys : ['triage_summary', 'call_state']).map(
                      (field) => (
                        <div key={field} className="space-y-1">
                          <Label className="font-mono text-xs">{field}</Label>
                          {contract?.enum_labels?.[field] ? (
                            <select
                              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                              value={priorFields[field] ?? ''}
                              onChange={(e) =>
                                setPriorFields((prev) => ({ ...prev, [field]: e.target.value }))
                              }
                            >
                              <option value="">—</option>
                              {Object.keys(contract.enum_labels[field]).map((v) => (
                                <option key={v} value={v}>
                                  {contract.enum_labels![field][v]} ({v})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <Input
                              value={priorFields[field] ?? ''}
                              onChange={(e) =>
                                setPriorFields((prev) => ({ ...prev, [field]: e.target.value }))
                              }
                              placeholder="Prior value"
                            />
                          )}
                        </div>
                      ),
                    )}
                  </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void onPreview()}
                    disabled={preview.isPending}
                  >
                    Run preview
                  </Button>
                  {previewOut ? (
                    <ListViewSwitcher
                      value={previewView}
                      onValueChange={(v) => setPreviewView(v)}
                      variant="labeled"
                      options={[
                        { value: 'structured', label: 'Structured' },
                        { value: 'json', label: 'JSON' },
                      ]}
                    />
                  ) : null}
                </div>
                {previewOut ? (
                  previewView === 'json' ? (
                    <pre className="max-h-64 overflow-auto rounded-md border border-border bg-muted/30 p-3 text-xs">
                      {JSON.stringify(previewOut, null, 2)}
                    </pre>
                  ) : (
                    <StructuredPreviewOutput output={previewOut} contract={contract} />
                  )
                ) : null}
              </div>
            </EntityManageSection>

            <EntityManageSection
              title="Revision history"
              subtitle={`${(corpus.revisions || []).length} revision(s)`}
              leadingIcon={History}
              collapsible
              defaultOpen={false}
            >
              <div className="max-h-48 space-y-2 overflow-auto text-sm">
                {(corpus.revisions || []).length === 0 ? (
                  <p className="text-muted-foreground">No revisions yet.</p>
                ) : (
                  (corpus.revisions || []).map((r) => (
                    <div key={r.id} className="border-b border-border pb-2 last:border-0">
                      <p className="font-medium">
                        v{r.version}{' '}
                        <span className="font-normal text-muted-foreground">({r.provenance})</span>
                      </p>
                      {r.change_summary ? (
                        <p className="text-muted-foreground">{r.change_summary}</p>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </EntityManageSection>
          </>
        }
      />
    </PageContainer>
  )
}
